import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import galleryData from '../../data/gallery.json';
import type { GalleryImage, GalleryData } from '../../types/gallery';

/**
 * Resolves the public URL for a gallery image
 * Images are stored in the public directory
 */
const getImageUrl = (filename: string): string => {
  return `/${filename}`;
};

/**
 * Gallery Component
 * 
 * Displays restaurant photos in a responsive grid layout with lightbox support.
 * Loads images from gallery.json and supports keyboard navigation in lightbox.
 * 
 * Requirements:
 * - 8.1: Display restaurant photos in a grid layout
 * - 8.2: Open enlarged view on image click
 * - 8.3: Previous/Next navigation in lightbox
 * - 8.4: Keyboard navigation (ArrowLeft, ArrowRight, Escape)
 * - 8.5: Responsive grid layout
 * - 8.6: Lazy loading for images
 */
export const Gallery: React.FC = () => {
  const { images } = galleryData as GalleryData;

  // Sort images by displayOrder
  const sortedImages: GalleryImage[] = [...images].sort(
    (a, b) => a.displayOrder - b.displayOrder
  );

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? sortedImages.length - 1 : prev - 1));
  }, [sortedImages.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === sortedImages.length - 1 ? 0 : prev + 1));
  }, [sortedImages.length]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          event.preventDefault();
          goToNext();
          break;
        // Escape is handled by the Modal component
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, goToPrevious, goToNext]);

  const currentImage = sortedImages[currentIndex];

  return (
    <>
      {/* Responsive Grid: 1 col mobile, 2 col tablet, 3 col desktop */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        role="list"
        aria-label="Restaurantfotos"
      >
        {sortedImages.map((image, index) => (
          <div
            key={image.id}
            role="listitem"
            className="group relative overflow-hidden rounded-lg cursor-pointer aspect-video bg-[#242424]"
            onClick={() => openLightbox(index)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openLightbox(index);
              }
            }}
            tabIndex={0}
            aria-label={`${image.alt} – Klicken zum Vergrößern`}
          >
            <img
              src={getImageUrl(image.filename)}
              alt={image.alt}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              width={image.width}
              height={image.height}
            />
            {/* Hover overlay */}
            <div
              className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
              aria-hidden="true"
            >
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <svg
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                </svg>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      <Modal
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        showCloseButton={false}
        className="!bg-transparent !shadow-none !max-w-5xl !p-0 !overflow-visible"
      >
        <div className="relative flex items-center justify-center">
          {/* Close button */}
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute -top-12 right-0 text-white hover:text-[#c9a84c] transition-colors focus:outline-none focus:ring-2 focus:ring-[#c9a84c] rounded-lg p-1"
            aria-label="Lightbox schließen"
          >
            <Icon name="close" size={28} decorative />
          </button>

          {/* Previous button */}
          {sortedImages.length > 1 && (
            <button
              type="button"
              onClick={goToPrevious}
              className="absolute left-0 -translate-x-14 text-white hover:text-[#c9a84c] transition-colors focus:outline-none focus:ring-2 focus:ring-[#c9a84c] rounded-lg p-2 bg-black/50 hover:bg-black/70"
              aria-label="Vorheriges Bild"
            >
              <Icon name="arrow-left" size={28} decorative />
            </button>
          )}

          {/* Image */}
          {currentImage && (
            <div className="w-full">
              <img
                src={getImageUrl(currentImage.filename)}
                alt={currentImage.alt}
                className="w-full max-h-[80vh] object-contain rounded-lg"
              />
              {/* Caption */}
              <p className="text-center text-gray-300 text-sm mt-3">
                {currentImage.alt}
              </p>
              {/* Counter */}
              <p className="text-center text-gray-500 text-xs mt-1" aria-live="polite">
                {currentIndex + 1} / {sortedImages.length}
              </p>
            </div>
          )}

          {/* Next button */}
          {sortedImages.length > 1 && (
            <button
              type="button"
              onClick={goToNext}
              className="absolute right-0 translate-x-14 text-white hover:text-[#c9a84c] transition-colors focus:outline-none focus:ring-2 focus:ring-[#c9a84c] rounded-lg p-2 bg-black/50 hover:bg-black/70"
              aria-label="Nächstes Bild"
            >
              <Icon name="arrow-right" size={28} decorative />
            </button>
          )}
        </div>
      </Modal>
    </>
  );
};
